import React, {useEffect, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faBell,
    faCalendarAlt,
    faChevronLeft,
    faChevronRight,
    faExclamationTriangle,
    faHashtag,
    faMapMarkerAlt,
    faSpinner,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import {getGeolocationFromLatLng} from '../utils/utility';
import {convertUtcToLocal} from '../utils/dateUtils';

const ITEMS_PER_PAGE = 5;

const AlertPopupDialog = ({ showPopup, togglePopup, popupData, clearNotifications }) => {
    const [locationData, setLocationData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (showPopup) {
            setIsLoading(true);
            setLocationData([]);
            setCurrentPage(1);
        }
    }, [showPopup]);

    useEffect(() => {
        if (popupData && popupData.length > 0) {
            const fetchLocationData = async () => {
                setIsLoading(true);
                const data = await Promise.all(
                    popupData.map(async (item) => {
                        const location = await getGeolocationFromLatLng(item.latitude, item.longitude);
                        return {
                            ...item,
                            location,
                            localDateTime: convertUtcToLocal(item.dateTime)
                        };
                    })
                );
                setLocationData(data);
                setIsLoading(false);
            };
            fetchLocationData();
        } else if (popupData && popupData.length === 0) {
            setLocationData([]);
            setIsLoading(false);
        }
    }, [popupData]);

    function parseAlertData(alertObj) {
        let alertType = '';
        alertType = !alertObj.lockStatus ? alertType + 'Lock Open, ' : alertType;
        alertType = !alertObj.backCoverStatus || alertObj.backCoverOpenAlert ? alertType + 'Back Cover Open, ' : alertType;
        alertType = alertObj.lowBatteryAlert ? alertType + 'Low Battery, ' : alertType;
        alertType = alertObj.lockRopeTamperAlert ? alertType + 'Lock Rope Tampered, ' : alertType;
        alertType = alertObj.vibrationAlert ? alertType + "Vibration Alert, " : alertType;
        alertType = alertObj.unauthorizedRfidAlert ? alertType + 'Unauthorized RFID.' : alertType;
        return alertType;
    }

    const validAlerts = locationData.filter((item) => {
        const alertType = parseAlertData(item).trim();
        return alertType && alertType !== ',';
    });

    const totalPages = Math.max(1, Math.ceil(validAlerts.length / ITEMS_PER_PAGE));
    const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedAlerts = validAlerts.slice(startIdx, startIdx + ITEMS_PER_PAGE);

    if (!showPopup) return null;

    return (
        <div className="alert-popup-backdrop" onClick={togglePopup}>
            <div className="popup-dialog" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="alert-popup-header">
                    <div className="alert-popup-title">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="alert-title-icon" />
                        <h3>Alerts</h3>
                        {!isLoading && validAlerts.length > 0 && (
                            <span className="alert-count-badge">{validAlerts.length}</span>
                        )}
                    </div>
                    <button className="modal-close-btn" onClick={togglePopup}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Content */}
                <div className="popup-content">
                    {isLoading ? (
                        <div className="alert-loading-state">
                            <FontAwesomeIcon icon={faSpinner} spin className="alert-loading-icon" />
                            <p>Fetching alerts...</p>
                        </div>
                    ) : validAlerts.length === 0 ? (
                        <div className="alert-empty-state">
                            <FontAwesomeIcon icon={faBell} className="alert-empty-icon" />
                            <p>No alerts to display</p>
                        </div>
                    ) : (
                        paginatedAlerts.map((item, index) => {
                            const alertType = parseAlertData(item).trim();
                            return (
                                <div className='notification-record' key={startIdx + index}>
                                    <div className="alert-record-row">
                                        <div className="alert-field">
                                            <FontAwesomeIcon icon={faHashtag} className="alert-field-icon" />
                                            <span className="alert-field-label">ID:</span>
                                            <span className="alert-field-value">{item.reportId}</span>
                                        </div>
                                        <div className="alert-field">
                                            <FontAwesomeIcon icon={faCalendarAlt} className="alert-field-icon" />
                                            <span className="alert-field-label">Date & Time:</span>
                                            <span className="alert-field-value">{item.localDateTime}</span>
                                        </div>
                                    </div>

                                    <div className="alert-record-row">
                                        <div className="alert-field">
                                            <FontAwesomeIcon icon={faMapMarkerAlt} className="alert-field-icon" />
                                            <span className="alert-field-label">Location:</span>
                                            <span className="alert-field-value">{item.location}</span>
                                        </div>
                                    </div>

                                    <div className="alert-record-row">
                                        <div className="alert-field alert-type-field">
                                            <FontAwesomeIcon icon={faBell} className="alert-field-icon alert-icon-warning" />
                                            <span className="alert-field-label">Alert:</span>
                                            <span className="alert-field-value alert-type-value">{alertType}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                {!isLoading && (
                    <div className="alert-popup-footer">
                        {validAlerts.length > ITEMS_PER_PAGE && (
                            <div className="alert-pagination">
                                <button
                                    className="alert-page-btn"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage((p) => p - 1)}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </button>
                                <span className="alert-page-info">
                                    {currentPage} of {totalPages}
                                </span>
                                <button
                                    className="alert-page-btn"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage((p) => p + 1)}
                                >
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        )}
                        <button className="yekkada-btn" onClick={clearNotifications}>
                            Clear Notifications
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AlertPopupDialog;
